// Global Instructions Rule Applied!
//
// The Pools table of the distribution form (waterfall phase 6 PR 3): one line
// per pool the distribution touches, between the Tiers strip and the
// Allocation grid, on a fund whose waterfall has pools.
//
// It is the two tables around it, by pool. From the Tiers strip: the bucket -
// Target (the capital contributed in the pool), Received before (what of it
// had been returned), This distribution (the pool's sources) and State after.
// From the Allocation grid: the tiers left to right, Partner and Carry, with
// what the pool's proceeds pay under each. The Cross-Pool Loss Offset tier's
// column carries both sides of an offset: on the pool that pays it "to
// <pool>", on the pool whose loss it offsets "<amount> from <pool>" - return
// of THAT pool's capital, which is why its State after moves.
//
// Everything is the server's (wf__distribution_pools): "before" from the pool
// results Compute froze on the distribution, the rest from the rows as they
// stand.

import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import NumberText from '../../../../core/components/NumberText.tsx'
import type {
  DistributionPool,
  DistributionTierStrip
} from '../../../../state_manager/data_models/distribution_detail.ts'
import { hasFigure, tierStateTag, tierTitle } from './allocationLabels.ts'

const { Text } = Typography

const dash = <Text type='secondary'>—</Text>

interface PoolsStripProps {
  pools: DistributionPool[]
  /** the tiers of the waterfall, as the strip above has them */
  tiers: DistributionTierStrip[]
}

const cellOf = (pool: DistributionPool, sequence: number) => pool.tiers.find((c) => c.tier_sequence === sequence)

export default function PoolsStrip({ pools, tiers }: PoolsStripProps) {
  if (pools.length === 0) return null
  const sortedTiers = [...tiers].sort((a, b) => a.tier_sequence - b.tier_sequence).filter((t) => t.prog__pays_lp || t.prog__pays_gp)
  const lossOffset = sortedTiers.find((t) => t.prog__is_loss_offset)

  const tierColumns: ColumnsType<DistributionPool> = sortedTiers.map((t) => ({
    title: tierTitle(t.tier_sequence, t.tier_type_label),
    key: `tier-${t.tier_sequence}`,
    children: [
      ...(t.prog__pays_lp
        ? [
            {
              title: 'Partner',
              key: `tier-${t.tier_sequence}-partner`,
              width: 170,
              align: 'right' as const,
              render: (_: unknown, pool: DistributionPool) => {
                const cell = cellOf(pool, t.tier_sequence)
                const paid = cell?.partner_effective ?? 0
                // the Cross-Pool Loss Offset tier: where the pool's money went,
                // and what other pools paid toward this pool's loss
                const out = t.prog__is_loss_offset ? pool.remedied_out_to : []
                const received = t.prog__is_loss_offset ? pool.remedied_in_from : []
                if (!hasFigure(paid) && out.length === 0 && received.length === 0) return dash
                return (
                  <div>
                    {hasFigure(paid) ? <NumberText value={paid} /> : dash}
                    {out.map((r) => (
                      <div key={`to-${r.pool_id}`}>
                        <Text type='secondary' className='text-xs'>
                          <NumberText value={r.amount} /> to {r.pool_code ?? 'another pool'}
                        </Text>
                      </div>
                    ))}
                    {received.map((r) => (
                      <div key={`from-${r.pool_id}`}>
                        <Text type='secondary' className='text-xs'>
                          <NumberText value={r.amount} /> from {r.pool_code ?? 'another pool'}
                        </Text>
                      </div>
                    ))}
                  </div>
                )
              }
            }
          ]
        : []),
      ...(t.prog__pays_gp
        ? [
            {
              title: 'Carry',
              key: `tier-${t.tier_sequence}-carry`,
              width: 140,
              align: 'right' as const,
              render: (_: unknown, pool: DistributionPool) => {
                const carry = cellOf(pool, t.tier_sequence)?.carry_effective ?? 0
                return hasFigure(carry) ? <NumberText value={carry} /> : dash
              }
            }
          ]
        : [])
    ]
  }))

  const columns: ColumnsType<DistributionPool> = [
    { title: 'Pool', key: 'pool', width: 110, fixed: 'left', render: (_: unknown, pool) => pool.pool_code ?? '—' },
    {
      title: 'Target',
      dataIndex: 'contributions',
      width: 150,
      align: 'right',
      render: (v: number) => <NumberText value={v} />
    },
    {
      title: 'Received before',
      dataIndex: 'returned_before',
      width: 150,
      align: 'right',
      render: (v: number) => <NumberText value={v} />
    },
    {
      title: 'This distribution',
      dataIndex: 'proceeds',
      width: 150,
      align: 'right',
      render: (v: number) => (hasFigure(v) ? <NumberText value={v} wrap={(f) => <Text strong>{f}</Text>} /> : dash)
    },
    ...tierColumns,
    {
      title: 'State after',
      key: 'state',
      width: 190,
      render: (_: unknown, pool) => {
        // the pool's capital after this distribution: returned in full, or what is still to go
        const state = hasFigure(pool.capital_due_after) ? 'open' : 'full'
        const tag = tierStateTag(state)
        if (!tag) return null
        return (
          <Tag color={tag.color}>
            {state === 'open' ? (
              <>
                <NumberText value={pool.capital_due_after} /> {tag.label}
              </>
            ) : (
              tag.label
            )}
          </Tag>
        )
      }
    }
  ]

  const leaves = sortedTiers.flatMap((t) => [
    ...(t.prog__pays_lp ? [{ tier: t, kind: 'partner' as const }] : []),
    ...(t.prog__pays_gp ? [{ tier: t, kind: 'carry' as const }] : [])
  ])
  const total = (pick: (pool: DistributionPool) => number) => pools.reduce((acc, p) => acc + pick(p), 0)

  return (
    <div className='mb-3' data-testid='pools-strip'>
      <Text strong>Pools</Text>
      <Table<DistributionPool>
        size='small'
        rowKey='pool_id'
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={pools}
        className='mt-1'
        summary={() =>
          pools.length > 1 ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} align='right'>
                  <NumberText value={total((p) => p.proceeds)} wrap={(f) => <Text strong>{f}</Text>} />
                </Table.Summary.Cell>
                {leaves.map(({ tier, kind }, i) => (
                  <Table.Summary.Cell key={`${tier.tier_sequence}-${kind}`} index={4 + i} align='right'>
                    <NumberText
                      value={total((p) => {
                        const cell = cellOf(p, tier.tier_sequence)
                        return cell ? (kind === 'partner' ? cell.partner_effective : cell.carry_effective) : 0
                      })}
                      wrap={(f) => <Text strong>{f}</Text>}
                    />
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={4 + leaves.length} />
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />
      <Text type='secondary' className='block mt-1'>
        A source runs through the tiers of its pool. The target is the capital contributed in the pool, and the pool
        returns it first.
        {lossOffset
          ? ` Under ${tierTitle(lossOffset.tier_sequence, lossOffset.tier_type_label)} a pool pays toward the realized losses of other pools - return of their capital, without carry - so the pool it pays is listed with what it received and its state after.`
          : ''}
      </Text>
    </div>
  )
}
